// Documents workspace for an OT. Surfaces every artifact tied to the
// order — slot docs, photos, redevances, empotage files — grouped by
// stage with inline preview, blocker highlights, search and bulk
// download. Lives in a near-full-screen modal so the user gets a
// proper bird's-eye view of every doc instead of scrolling a narrow
// side panel.

import { useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import {
  AlertTriangle,
  Check,
  Download,
  Eye,
  FileText,
  Loader2,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { DocPreviewDialog } from "@/features/transit/components/doc-preview-dialog"
import { UploadMetadataDialog } from "@/features/transit/components/upload-metadata-dialog"
import { useUploadWithMetadata } from "@/features/transit/hooks/use-upload-with-metadata"
import { getDownloadUrl } from "@/features/transit/api/transit.api"
import { parseBlocker } from "@/features/transit/lib/blockers"
import { resolveApiError } from "@/lib/api/error-i18n"
import {
  otDocSlots,
  otDocSlotsForStage,
  totalDocProgress,
} from "@/features/transit/lib/doc-slots"
import { isOTLocked } from "@/features/transit/lib/ot-math"
import { OT_STAGES, stageIndex } from "@/features/transit/lib/stages"
import { useTransitUpload } from "@/features/transit/hooks/use-transit-upload"
import {
  useDeleteEmpotageFile,
  useDeletePhoto,
  useDeleteRedevance,
  useDeleteSlotDoc,
  useUpsertSlotDoc,
} from "@/features/transit/hooks/use-doc-mutations"
import { cn } from "@/lib/utils"
import type {
  EmpotageFileItem,
  OT,
  OTDocSlotId,
  OTSlotDoc,
  OTStage,
  PhotoItem,
  RedevanceItem,
} from "@/features/transit/api/types"

interface DocumentsSidePanelProps {
  ot: OT
  // Optional controlled state so other parts of the page (e.g. the
  // sticky footer's "Open documents" CTA) can open the modal too.
  open?: boolean
  onOpenChange?: (open: boolean) => void
  // When provided, the trigger button is suppressed — the parent owns
  // opening the modal.
  triggerless?: boolean
}

// Internal alias — the component is now a modal, but the export name is
// kept stable so the rest of the app doesn't need to update its imports.

// Internal flat representation for every visible row. Lets us share
// search, filter, preview and delete handling regardless of where the
// underlying record lives in the OT graph.
type DocRowKind = "slot" | "photo" | "redevance" | "empotageFile"
interface PreviewableDoc {
  name: string
  storedKey: string
  docType?: string | null
  uploadedAt?: string | null
  uploadedBy?: string | null
  note?: string | null
}
interface DocRow {
  kind: DocRowKind
  // Stable id used for React keys + delete payloads.
  id: string
  // Slot id when kind === "slot"; otherwise undefined.
  slotId?: OTDocSlotId
  // Translation key for the title (slot id) or a literal label.
  title: string
  // Primary stage this doc belongs to.
  stage: OTStage
  // Whether the row currently has an uploaded file.
  filled: boolean
  // Reference to the underlying record when filled. `null` keeps the
  // shape compatible with the preview dialog's prop, which accepts null.
  doc: PreviewableDoc | null
  // Whether this row is *required* and the missing-doc shows up in blockers[].
  isBlocker: boolean
}

type Filter = "all" | "missing" | "uploaded"

const FILTERS: Filter[] = ["all", "missing", "uploaded"]

export function DocumentsSidePanel({
  ot,
  open,
  onOpenChange,
  triggerless,
}: DocumentsSidePanelProps) {
  const { t } = useTranslation("transit")
  const totals = totalDocProgress(ot)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {triggerless ? null : (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <FileText className="size-4" />
            {t("docsPanel.btn", {
              filled: totals.filled,
              total: totals.total,
            })}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="flex h-[90vh] w-[95vw] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl">
        <SidePanelBody ot={ot} />
      </DialogContent>
    </Dialog>
  )
}

function SidePanelBody({ ot }: { ot: OT }) {
  const { t } = useTranslation("transit")
  const [filter, setFilter] = useState<Filter>("all")
  const [search, setSearch] = useState("")
  const [previewDoc, setPreviewDoc] = useState<PreviewableDoc | null>(null)

  const [downloadAllPending, setDownloadAllPending] = useState(false)

  const totals = totalDocProgress(ot)
  const blockerSlots = useMemo(() => extractBlockerSlots(ot), [ot])
  const grouped = useMemo(
    () =>
      buildGroupedRows(ot, blockerSlots).map((g) => ({
        ...g,
        stageStateLabel: t(`docsPanel.${g.stageStateLabel}`),
      })),
    [ot, blockerSlots, t],
  )

  // Apply search + filter to the flattened structure. Search is
  // intentionally narrow — it matches against the visible document
  // *label* only (the localised slot name, or the literal name for
  // non-slot artifacts). Filename and uploader are intentionally
  // excluded so the user can find a doc by what they SEE on the card.
  const visibleStages = useMemo(() => {
    const q = search.trim().toLowerCase()
    return grouped
      .map((g) => ({
        ...g,
        rows: g.rows.filter((r) => {
          if (filter === "missing" && r.filled) return false
          if (filter === "uploaded" && !r.filled) return false
          if (q.length > 0) {
            // For slot docs, search the *translated* label so French
            // users find "instruction" in "Instruction d'embarquement"
            // and English users find "shipping" in "Shipping instruction".
            const label =
              r.kind === "slot"
                ? t(`docLabels.${r.title as OTDocSlotId}`, {
                    defaultValue: r.title,
                  })
                : r.title
            if (!label.toLowerCase().includes(q)) return false
          }
          return true
        }),
      }))
      .filter((g) => g.rows.length > 0)
  }, [grouped, filter, search, t])

  // Bulk-download → ZIP. Lazy-loads jszip on first click so the bundle
  // hit only happens when the user actually wants the feature.
  const downloadAll = async () => {
    const targets = visibleStages
      .flatMap((g) =>
        g.rows
          .filter((r) => r.filled && r.doc)
          .map((r) => ({
            stage: g.stage,
            doc: r.doc!,
          })),
      )
    if (targets.length === 0) return
    setDownloadAllPending(true)
    try {
      const { default: JSZip } = await import("jszip")
      const zip = new JSZip()

      // Fetch presigned URLs in parallel, then stream into the ZIP. We
      // group files by stage folder so the recipient can navigate them.
      const usedNames = new Map<string, number>()
      const uniqueName = (folder: string, name: string) => {
        const key = `${folder}/${name}`
        const count = (usedNames.get(key) ?? 0) + 1
        usedNames.set(key, count)
        if (count === 1) return name
        const dot = name.lastIndexOf(".")
        if (dot < 0) return `${name} (${count})`
        return `${name.slice(0, dot)} (${count})${name.slice(dot)}`
      }

      const tasks = targets.map(async ({ stage, doc }) => {
        const folder = t(`stages.${stage}`)
        const filename = uniqueName(folder, doc.name)
        const { url } = await getDownloadUrl(ot.id, doc.storedKey)
        const res = await fetch(url)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const blob = await res.blob()
        zip.folder(folder)?.file(filename, blob)
      })

      const settled = await Promise.allSettled(tasks)
      const failed = settled.filter((s) => s.status === "rejected").length
      if (failed === targets.length) {
        toast.error(t("docsPanel.downloadAllFailed"))
        return
      }
      if (failed > 0) {
        toast.warning(
          t("docsPanel.downloadAllPartial", {
            failed,
            total: targets.length,
          }),
        )
      }

      const archive = await zip.generateAsync({ type: "blob" })
      const objectUrl = URL.createObjectURL(archive)
      const a = document.createElement("a")
      a.href = objectUrl
      a.download = `${ot.otNumber}-documents.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (e) {
      toast.error(resolveApiError(e, t))
    } finally {
      setDownloadAllPending(false)
    }
  }

  const allUploadedCount = grouped
    .flatMap((g) => g.rows)
    .filter((r) => r.filled).length
  const allMissingCount = grouped
    .flatMap((g) => g.rows)
    .filter((r) => !r.filled).length

  return (
    <>
      <DialogHeader className="border-b px-6 pt-6 pb-4">
        {/* Leave room on the right for the close button rendered by
            DialogContent (top-right X). Don't crowd it with actions. */}
        <DialogTitle className="pr-10">{t("docsPanel.title")}</DialogTitle>
        <DialogDescription className="text-xs">
          {totals.filled === totals.total
            ? t("docsPanel.summaryAllUploaded", { total: totals.total })
            : t("docsPanel.summary", {
                filled: totals.filled,
                total: totals.total,
                missing: totals.total - totals.filled,
              })}
        </DialogDescription>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1 max-w-md">
            <Search className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("docsPanel.searchPlaceholder")}
              className="h-8 pl-8 pr-7 text-xs"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="text-muted-foreground hover:text-foreground absolute right-2 top-1/2 -translate-y-1/2"
                aria-label={t("docsPanel.clearSearch")}
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>

          <div className="bg-muted inline-flex h-8 items-center rounded-md p-1">
            {FILTERS.map((f) => {
              const count =
                f === "missing"
                  ? allMissingCount
                  : f === "uploaded"
                    ? allUploadedCount
                    : grouped.reduce((acc, g) => acc + g.rows.length, 0)
              const active = filter === f
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={cn(
                    "h-full rounded-sm px-2.5 text-[11px] font-medium uppercase tracking-wide transition-colors",
                    active
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t(`docsPanel.filters.${f}`)}
                  <span className="ml-1 tabular-nums opacity-70">{count}</span>
                </button>
              )
            })}
          </div>

          <Button
            variant="outline"
            size="sm"
            className="h-8 ml-auto"
            onClick={downloadAll}
            disabled={
              downloadAllPending ||
              visibleStages.flatMap((g) => g.rows).filter((r) => r.filled)
                .length === 0
            }
          >
            {downloadAllPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Download className="size-3.5" />
            )}
            {t("docsPanel.downloadAll")}
          </Button>
        </div>
      </DialogHeader>

      {/* `min-h-0` lets this flex child actually shrink so the ScrollArea
          can take a bounded height; without it, content pushes the modal
          past its 90vh and the inner viewport never scrolls. */}
      <ScrollArea className="min-h-0 flex-1">
        <div className="px-6 py-5">
          {visibleStages.length === 0 ? (
            <p className="text-muted-foreground py-12 text-center text-sm">
              {search.trim() ? t("docsPanel.noMatch") : t("docsPanel.empty")}
            </p>
          ) : (
            <div className="flex flex-col gap-6">
              {visibleStages.map((group) => (
                <StageGroup
                  key={group.stage}
                  ot={ot}
                  stage={group.stage}
                  rows={group.rows}
                  stageStateLabel={group.stageStateLabel}
                  onPreview={setPreviewDoc}
                />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      <DocPreviewDialog
        otId={ot.id}
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </>
  )
}

// ── Per-stage group ────────────────────────────────────────────────

interface GroupedStage {
  stage: OTStage
  rows: DocRow[]
  stageStateLabel: string
}

function StageGroup({
  ot,
  stage,
  rows,
  stageStateLabel,
  onPreview,
}: {
  ot: OT
  stage: OTStage
  rows: DocRow[]
  stageStateLabel: string
  onPreview: (doc: PreviewableDoc | null) => void
}) {
  const { t } = useTranslation("transit")
  const filled = rows.filter((r) => r.filled).length
  const total = rows.length

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <div className="text-foreground text-sm font-medium">
            {t(`stages.${stage}`)}
          </div>
          <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
            {stageStateLabel}
          </div>
        </div>
        <span className="text-muted-foreground text-xs tabular-nums">
          {filled}/{total}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
        {rows.map((row) => (
          <DocRowCard
            key={row.id}
            ot={ot}
            row={row}
            onPreview={() => onPreview(row.doc)}
          />
        ))}
      </div>
    </section>
  )
}

// ── Individual row card ────────────────────────────────────────────

function DocRowCard({
  ot,
  row,
  onPreview,
}: {
  ot: OT
  row: DocRow
  onPreview: () => void
}) {
  const { t } = useTranslation("transit")
  const upload = useTransitUpload()
  const upsertSlot = useUpsertSlotDoc(ot.id)
  const deleteSlot = useDeleteSlotDoc(ot.id)
  const deletePhoto = useDeletePhoto(ot.id)
  const deleteEmpotageFile = useDeleteEmpotageFile(ot.id)
  const deleteRedevance = useDeleteRedevance(ot.id)

  // Empty-state uploads from this modal only fire for slot-doc rows; the
  // JSONB lists (photos / empotageFiles / redevances) only show as
  // already-filled rows here. Their inputs live on their respective
  // panels with the right context (e.g. ticket vs rapport for empotage).
  const labelText =
    row.kind === "slot"
      ? t(`docLabels.${row.title as OTDocSlotId}`, { defaultValue: row.title })
      : row.kind === "empotageFile" && row.title === "rapportEmpotage"
        ? t("empotage.rapportTitle")
        : row.kind === "empotageFile" && row.title === "ticketPesee"
          ? t("empotage.ticketsTitle")
          : row.title

  const meta = useUploadWithMetadata({
    kindLabel: labelText,
    upload: async (file, m) => {
      if (row.kind !== "slot" || !row.slotId) return
      await upload.upload({
        otId: ot.id,
        kind: "slotDoc",
        slotId: row.slotId,
        file,
        register: async ({ storedKey, file: f }) =>
          upsertSlot.mutateAsync({
            slotId: row.slotId!,
            payload: {
              name: m.name,
              storedKey,
              docType: f.type || undefined,
              size: f.size,
              uploadedBy: m.uploadedBy || undefined,
              note: m.description || undefined,
            },
          }),
      })
    },
  })

  const onDelete = () => {
    if (row.kind === "slot" && row.slotId) deleteSlot.mutate(row.slotId)
    else if (row.kind === "photo") deletePhoto.mutate(row.id)
    else if (row.kind === "empotageFile") deleteEmpotageFile.mutate(row.id)
    else if (row.kind === "redevance") deleteRedevance.mutate(row.id)
  }

  const isUploading = upload.isUploading || upsertSlot.isPending
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const locked = isOTLocked(ot)

  // Empty slot rows make the entire card a click target for the file
  // picker — the small "Upload" affordance on the right is too easy to
  // miss when the round status indicator on the left looks like a
  // checkbox. Filled rows keep their inner Eye / Trash actions. Locked
  // OTs disable the picker entirely and hide write affordances.
  const triggerPicker = () => {
    if (isUploading || locked) return
    fileInputRef.current?.click()
  }

  const cardClassName = cn(
    "group flex items-center gap-3 rounded-md border px-3 py-2 transition-colors",
    row.filled
      ? "bg-card hover:bg-muted/30"
      : locked
        ? "border-dashed bg-muted/30"
        : "border-dashed bg-card hover:bg-muted/30 cursor-pointer text-left w-full",
  )

  const cardBody = (
    <>
      <RowStatus filled={row.filled} blocker={row.isBlocker} />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-foreground truncate text-sm font-medium">
            {labelText}
          </span>
          <KindBadge row={row} />
        </div>
        {row.filled && row.doc ? (
          <>
            <button
              type="button"
              onClick={onPreview}
              className="text-muted-foreground hover:text-foreground mt-0.5 flex w-full items-center gap-1 text-left text-[11px] tabular-nums hover:underline"
            >
              <span className="truncate font-mono">{row.doc.name}</span>
              <span className="text-muted-foreground/70 flex-shrink-0">
                · {row.doc.uploadedAt?.slice(0, 10) ?? "—"}
                {row.doc.uploadedBy ? ` · ${row.doc.uploadedBy}` : ""}
              </span>
            </button>
            {row.doc.note ? (
              <p className="text-muted-foreground/80 mt-0.5 line-clamp-2 text-[11px]">
                {row.doc.note}
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="flex flex-shrink-0 items-center gap-0.5">
        {row.filled && row.doc ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={onPreview}
                  aria-label={t("preview.btn")}
                >
                  <Eye className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("preview.btn")}</TooltipContent>
            </Tooltip>
            {locked ? null : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 hover:text-red-600"
                    onClick={onDelete}
                    aria-label={t("docCard.delete")}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("docCard.replaceHint")}</TooltipContent>
              </Tooltip>
            )}
          </>
        ) : locked ? (
          <span className="text-muted-foreground/70 text-[11px] italic">
            {t("locked.notProvided")}
          </span>
        ) : (
          <span
            className={cn(
              "border-input bg-background pointer-events-none inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium",
              isUploading && "opacity-60",
            )}
          >
            {isUploading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Upload className="size-4" />
            )}
            {t("docCard.upload")}
          </span>
        )}
      </div>
    </>
  )

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) meta.onPick(file)
          if (fileInputRef.current) fileInputRef.current.value = ""
        }}
      />
      {row.filled || locked ? (
        <div className={cardClassName}>{cardBody}</div>
      ) : (
        <button
          type="button"
          onClick={triggerPicker}
          disabled={isUploading}
          className={cardClassName}
        >
          {cardBody}
        </button>
      )}

      <UploadMetadataDialog
        pending={meta.pending}
        onCancel={meta.onCancel}
        onConfirm={meta.onConfirm}
      />
    </>
  )
}

function RowStatus({
  filled,
  blocker,
}: {
  filled: boolean
  blocker: boolean
}) {
  const { t } = useTranslation("transit")

  if (filled) {
    return (
      <span className="bg-emerald-600 text-white flex size-4 flex-shrink-0 items-center justify-center rounded-full">
        <Check className="size-2.5" />
      </span>
    )
  }
  if (blocker) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="bg-amber-500 text-white flex size-4 flex-shrink-0 items-center justify-center rounded-full">
            <AlertTriangle className="size-2.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent>{t("docsPanel.blockerHint")}</TooltipContent>
      </Tooltip>
    )
  }
  return (
    <span className="border-muted-foreground/40 size-4 flex-shrink-0 rounded-full border-2" />
  )
}

function KindBadge({ row }: { row: DocRow }) {
  const { t } = useTranslation("transit")
  if (row.kind === "slot") return null
  const labelKey =
    row.kind === "photo"
      ? "docsPanel.kindBadge.photo"
      : row.kind === "redevance"
        ? "docsPanel.kindBadge.redevance"
        : "docsPanel.kindBadge.empotageFile"
  return (
    <span className="text-muted-foreground bg-muted rounded px-1 py-px text-[9px] uppercase tracking-wide flex-shrink-0">
      {t(labelKey)}
    </span>
  )
}

// ── Aggregation ────────────────────────────────────────────────────

// Returns the set of slot ids that currently appear in `ot.blockers[]`
// as `missingDoc:<slotId>` codes. Used to highlight critical missing docs.
function extractBlockerSlots(ot: OT): Set<OTDocSlotId> {
  const out = new Set<OTDocSlotId>()
  for (const b of ot.blockers ?? []) {
    const parsed = parseBlocker(b)
    if (parsed.kind === "missingDoc") out.add(parsed.slotId)
  }
  return out
}

// Build the per-stage groups in stage order. Each stage gets:
//   - its slot rows (from doc-slots registry, fumigation-aware)
//   - its photos (only EMPOTAGE)
//   - its redevances (only FORMULE)
//   - its empotage files (only EMPOTAGE)
function buildGroupedRows(
  ot: OT,
  blockerSlots: Set<OTDocSlotId>,
): GroupedStage[] {
  const docsBySlot: Record<string, OTSlotDoc> = {}
  for (const d of ot.docs ?? []) docsBySlot[d.slotId] = d
  const slotsForOT = new Set(otDocSlots(ot).map((s) => s.id))

  const currentIdx = stageIndex(ot.stage)
  const groups: GroupedStage[] = []

  for (const stage of OT_STAGES) {
    const rows: DocRow[] = []

    // Slot docs assigned to this stage that are valid for this OT
    // (i.e. respect the fumigation toggle).
    for (const slot of otDocSlotsForStage(ot, stage)) {
      if (!slotsForOT.has(slot.id)) continue
      const existing = docsBySlot[slot.id]
      rows.push({
        kind: "slot",
        id: `slot:${slot.id}`,
        slotId: slot.id,
        title: slot.id,
        stage,
        filled: !!existing,
        doc: existing
          ? {
              name: existing.name,
              storedKey: existing.storedKey,
              docType: existing.docType,
              uploadedAt: existing.uploadedAt,
              uploadedBy: existing.uploadedBy,
              note: existing.note,
            }
          : null,
        isBlocker: blockerSlots.has(slot.id),
      })
    }

    if (stage === "FORMULE") {
      for (const r of (ot.redevances ?? []) as RedevanceItem[]) {
        rows.push({
          kind: "redevance",
          id: r.id,
          title: r.name,
          stage,
          filled: true,
          doc: {
            name: r.name,
            storedKey: r.storedKey,
            docType: r.docType,
            uploadedAt: r.uploadedAt,
            uploadedBy: r.uploadedBy ?? null,
          },
          isBlocker: false,
        })
      }
    }

    if (stage === "EMPOTAGE") {
      for (const p of (ot.photos ?? []) as PhotoItem[]) {
        rows.push({
          kind: "photo",
          id: p.id,
          title: p.name,
          stage,
          filled: true,
          doc: {
            name: p.name,
            storedKey: p.storedKey,
            docType: p.docType,
            uploadedAt: p.uploadedAt,
            uploadedBy: p.uploadedBy ?? null,
            note: p.note,
          },
          isBlocker: false,
        })
      }
      for (const f of (ot.empotageFiles ?? []) as EmpotageFileItem[]) {
        rows.push({
          kind: "empotageFile",
          id: f.id,
          // Show the artifact's purpose first, name in metadata line.
          title:
            f.kind === "rapportEmpotage"
              ? "rapportEmpotage"
              : f.kind === "ticketPesee"
                ? "ticketPesee"
                : f.name,
          stage,
          filled: true,
          doc: {
            name: f.name,
            storedKey: f.storedKey,
            docType: f.docType,
            uploadedAt: f.uploadedAt,
            uploadedBy: f.uploadedBy ?? null,
            note: f.note,
          },
          isBlocker: false,
        })
      }
    }

    if (rows.length === 0) continue

    const idx = stageIndex(stage)
    const stageStateLabel =
      idx < currentIdx
        ? "stageDone"
        : idx === currentIdx
          ? "stageCurrent"
          : "stageUpcoming"

    groups.push({
      stage,
      rows,
      stageStateLabel,
    })
  }

  return groups
}

